import { lazy, Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { FormType } from '@common/entities';
import { EntrataSession } from 'auth/EntrataSession';
import axios from 'axios';
import { ENTRATA_LOGIN_REQUIRED_MESSAGE, get } from 'config';
import { BASE_URL } from 'config/URL';
import { NotificationStatus } from 'enums';
import { setIsAuthenticated } from 'state/slices/authSlice';
import { setUserData } from 'state/slices/userSlice';
import { RootState } from 'state/store';
import PreviewResponsiveWrapper from 'views/application-preview/PreviewResponsiveWrapper';
import ViewSmartRoute from 'views/smart-routes/ViewSmartRoute';

import { BaseLayout } from 'components/layout';
import Loader from 'components/Loader';
import notification from 'components/notification';
import PageNotFound from 'components/PageNotFound';
import type { AuthMeResponse } from 'types/auth';

const DashBoard = lazy(() => import('views/dashboard/DashBoard'));
const ApplicationTemplate = lazy(() => import('views/ApplicationTemplate'));
const NewSmartRoute = lazy(() => import('views/smart-routes/NewSmartRoute'));
const ApplicationForm = lazy(() => import('views/application-form/ApplicationForm'));
const FormBuilder = lazy(() => import('views/form-builder/FormBuilder'));
const SdkIntegrationGuide = lazy(() => import('views/guides/SdkIntegrationGuide'));

function App() {
  const [useMockLogin, setUseMockLogin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [authVerificationCompleted, setAuthVerificationCompleted] = useState(false);
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const verify = async (token: string) => {
    try {
      if (!token) throw new Error('Token is missing.');

      const response = await axios.post(`${BASE_URL}/auth/verifyToken`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status !== 201) throw new Error('Failed to verify token');
      dispatch(setIsAuthenticated(true));
      dispatch(setUserData(response.data));
    } catch (err) {
      EntrataSession.setToken('');
      notification('Error', err.message, NotificationStatus.ERROR);
    } finally {
      setIsLoading(false);
      setAuthVerificationCompleted(true);
    }
  };

  /**
   * Fetches the authenticated user from the /auth/me endpoint.
   * The auth cookie is set by the verify-session redirect from Entrata.
   */
  const fetchAuthenticatedUser = async () => {
    try {
      const response = await get<AuthMeResponse>('/auth/me');

      EntrataSession.setUser(response.data.user);
      if (response.data.entrataRedirectLink) {
        EntrataSession.setEntrataUrl(response.data.entrataRedirectLink);
      }
      dispatch(setUserData(response.data.user));
      dispatch(setIsAuthenticated(true));
    } catch (err) {
      if (err instanceof Error && err.message === ENTRATA_LOGIN_REQUIRED_MESSAGE) {
        notification('Error', err.message, NotificationStatus.ERROR);
      }
    } finally {
      setIsLoading(false);
      setAuthVerificationCompleted(true);
    }
  };

  useEffect(() => {
    const isMockLogin =
      process.env.NODE_ENV === 'development' && process.env.USE_MOCK_LOGIN === 'true';
    setUseMockLogin(isMockLogin);

    if (isMockLogin) {
      setAuthVerificationCompleted(true);
      setIsLoading(false);
      return;
    }

    async function doSignIn() {
      // Legacy token-based authentication (if token exists in sessionStorage)
      if (EntrataSession.token) {
        setIsLoading(true);
        verify(EntrataSession.token);
        return;
      }

      // Cookie-based authentication via /auth/me
      // The auth cookie is set by verify-session when redirected from Entrata
      setIsLoading(true);
      fetchAuthenticatedUser();
    }

    doSignIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authVerificationCompleted || isLoading) return <Loader />;

  // Clean up any query parameters from the URL (e.g., from legacy flows)
  if (isAuthenticated && !useMockLogin && process.env.NODE_ENV === 'development') {
    window.history.replaceState(null, '', window.location.pathname);
  }

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        {isAuthenticated ? (
          <Route path="/">
            <Route
              index
              element={
                <BaseLayout>
                  <DashBoard />
                </BaseLayout>
              }
            />
            <Route path="application">
              <Route
                path="templates"
                index
                element={
                  <BaseLayout>
                    <ApplicationTemplate />
                  </BaseLayout>
                }
              />
              <Route
                path="details"
                element={
                  <BaseLayout>
                    <ApplicationForm />
                  </BaseLayout>
                }
              />
              <Route
                path="form-builder/:applicationId/:versionId"
                element={
                  <BaseLayout>
                    <FormBuilder />
                  </BaseLayout>
                }
              />
            </Route>
            <Route path="routes">
              <Route
                path="new-smart-route"
                index
                element={
                  <BaseLayout>
                    <NewSmartRoute />
                  </BaseLayout>
                }
              />
              <Route path="route/:routeId/:versionId" index element={<ViewSmartRoute />} />
              <Route
                path="preview/:routeId/:versionId"
                element={<PreviewResponsiveWrapper formType={FormType.Route} />}
              />
            </Route>
            <Route path="guides">
              <Route
                path="sdk-integration"
                element={
                  <BaseLayout>
                    <SdkIntegrationGuide />
                  </BaseLayout>
                }
              />
            </Route>
            <Route
              path="/application/preview/:applicationId/:versionId"
              element={
                <BaseLayout>
                  <PreviewResponsiveWrapper formType={FormType.Application} />
                </BaseLayout>
              }
            />
            <Route
              path="*"
              element={
                <BaseLayout>
                  <PageNotFound />
                </BaseLayout>
              }
            />
          </Route>
        ) : (
          <Route path="*" element={<PageNotFound />} />
        )}
      </Route>,
    ),
  );

  return (
    <Suspense fallback={<Loader />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}

export default App;
